/** How to order / pick the next off-screen item. */
export type OffscreenPlayMode =
  | 'loop_one'
  | 'newest_first'
  | 'oldest_first'
  | 'random_spine'
  | 'favorites';

/** After one item finishes: keep going, or wait for user. */
export type OffscreenAdvanceMode = 'auto' | 'manual';

export type OffscreenQualityFlag = 'too_hard' | 'too_easy' | 'poor_quality' | 'just_right';

export type OffscreenFeedbackEntry = {
  particleId: string;
  flags: OffscreenQualityFlag[];
  updatedAt: string;
};

export type OffscreenReleaseEntry = {
  particleId: string;
  releasedCount: number;
  firstReleaseCount: number;
  secondReviewCount: number;
  lastReleasedAt: string;
  lastCards?: Array<{ question: string; answer: string; glanceText?: string }>;
};

export type PersistedOffscreenCard = {
  subject: string;
  question: string;
  answer: string;
  title: string;
  glanceText?: string;
  glanceImageUrl?: string;
  particleId?: string;
  createdAt: string;
};

export type OffscreenPlaylist = {
  id: string;
  name: string;
  particleIds: string[];
  updatedAt: string;
};

export type OffscreenLibraryState = {
  playMode: OffscreenPlayMode;
  advanceMode: OffscreenAdvanceMode;
  answerDelay: number | 'manual';
  favoriteIds: string[];
  feedbackByParticleId: Record<string, OffscreenFeedbackEntry>;
  releaseByParticleId: Record<string, OffscreenReleaseEntry>;
  generatedCards: PersistedOffscreenCard[];
  playlists: OffscreenPlaylist[];
  activePlaylistId: string;
  showLyrics: boolean;
  /** Session-style empty for builtin「全部口播卡」after user clears the queue. */
  clearedBuiltinAll?: boolean;
};

const STORAGE_KEY = 'osr_offscreen_library_v1';

const DEFAULT_STATE: OffscreenLibraryState = {
  playMode: 'newest_first',
  advanceMode: 'manual',
  answerDelay: 5,
  favoriteIds: [],
  feedbackByParticleId: {},
  releaseByParticleId: {},
  generatedCards: [],
  playlists: [],
  activePlaylistId: 'all',
  showLyrics: false,
};

let memory: OffscreenLibraryState = {
  ...DEFAULT_STATE,
  favoriteIds: [],
  playlists: [],
  feedbackByParticleId: {},
  releaseByParticleId: {},
  generatedCards: [],
};

export function isOffscreenPlayMode(value: unknown): value is OffscreenPlayMode {
  return (
    value === 'newest_first'
    || value === 'oldest_first'
    || value === 'random_spine'
    || value === 'favorites'
  );
}

export function isOffscreenAdvanceMode(value: unknown): value is OffscreenAdvanceMode {
  return value === 'auto' || value === 'manual';
}

export function getMemoryOffscreenLibrary(): OffscreenLibraryState {
  return memory;
}

function normalizeState(raw: Partial<OffscreenLibraryState> | null | undefined): OffscreenLibraryState {
  const favoriteIds = Array.isArray(raw?.favoriteIds)
    ? [...new Set(raw!.favoriteIds.map((id) => String(id || '').trim()).filter(Boolean))]
    : [];
  const feedbackByParticleId: Record<string, OffscreenFeedbackEntry> = {};
  const feedbackSrc = raw?.feedbackByParticleId && typeof raw.feedbackByParticleId === 'object'
    ? raw.feedbackByParticleId
    : {};
  for (const [key, entry] of Object.entries(feedbackSrc)) {
    const particleId = String(key || '').trim();
    if (!particleId || !entry) continue;
    const flags = Array.isArray(entry.flags)
      ? entry.flags.filter((f): f is OffscreenQualityFlag => (
        f === 'too_hard' || f === 'too_easy' || f === 'poor_quality'
      ))
      : [];
    feedbackByParticleId[particleId] = {
      particleId,
      flags,
      updatedAt: String(entry.updatedAt || new Date().toISOString()),
    };
  }
  const releaseByParticleId: Record<string, OffscreenReleaseEntry> = {};
  const releaseSrc = raw?.releaseByParticleId && typeof raw.releaseByParticleId === 'object'
    ? raw.releaseByParticleId
    : {};
  for (const [key, entry] of Object.entries(releaseSrc)) {
    const particleId = String(key || '').trim();
    if (!particleId || !entry) continue;
    releaseByParticleId[particleId] = {
      particleId,
      releasedCount: Math.max(Number(entry.releasedCount) || 0, 0),
      firstReleaseCount: Math.max(Number(entry.firstReleaseCount) || 0, 0),
      secondReviewCount: Math.max(Number(entry.secondReviewCount) || 0, 0),
      lastReleasedAt: String(entry.lastReleasedAt || new Date(0).toISOString()),
      lastCards: Array.isArray(entry.lastCards) ? entry.lastCards.slice(0, 12) : undefined,
    };
  }
  const generatedCards = Array.isArray(raw?.generatedCards)
    ? raw.generatedCards.filter((card): card is PersistedOffscreenCard => (
      Boolean(card && typeof card.question === 'string' && typeof card.answer === 'string')
    )).slice(-500)
    : [];
  const playlists = Array.isArray(raw?.playlists)
    ? raw!.playlists
      .map((p) => ({
        id: String(p?.id || '').trim(),
        name: String(p?.name || '').trim() || '播放列表',
        particleIds: Array.isArray(p?.particleIds)
          ? [...new Set(p.particleIds.map((id) => String(id || '').trim()).filter(Boolean))]
          : [],
        updatedAt: String(p?.updatedAt || new Date().toISOString()),
      }))
      .filter((p) => p.id && p.id !== 'favorites' && p.id !== 'all')
    : [];

  return {
    playMode: isOffscreenPlayMode(raw?.playMode) ? raw!.playMode : 'newest_first',
    advanceMode: isOffscreenAdvanceMode(raw?.advanceMode) ? raw!.advanceMode : 'manual',
    answerDelay: raw?.answerDelay === 'manual' ? 'manual' : typeof raw?.answerDelay === 'number' && Number.isFinite(raw.answerDelay) ? Math.max(0, Math.min(300, Math.round(raw.answerDelay))) : 5,
    favoriteIds,
    feedbackByParticleId,
    releaseByParticleId,
    generatedCards,
    playlists,
    activePlaylistId: String(raw?.activePlaylistId || 'all').trim() || 'all',
    showLyrics: raw?.showLyrics === true,
    clearedBuiltinAll: raw?.clearedBuiltinAll === true,
  };
}

export function appendGeneratedCards(
  state: OffscreenLibraryState,
  cards: PersistedOffscreenCard[],
): OffscreenLibraryState {
  return { ...state, generatedCards: [...state.generatedCards, ...cards].slice(-500) };
}

export function markParticlesReleased(
  state: OffscreenLibraryState,
  releases: Array<{ particleId: string; cards?: Array<{ question: string; answer: string; glanceText?: string }> }>,
): OffscreenLibraryState {
  const now = new Date().toISOString();
  const releaseByParticleId = { ...state.releaseByParticleId };
  for (const release of releases) {
    const particleId = String(release.particleId || '').trim();
    if (!particleId) continue;
    const previous = releaseByParticleId[particleId];
    releaseByParticleId[particleId] = {
      particleId,
      releasedCount: (previous?.releasedCount || 0) + 1,
      firstReleaseCount: previous?.firstReleaseCount ?? 1,
      secondReviewCount: (previous?.secondReviewCount || 0) + (previous ? 1 : 0),
      lastReleasedAt: now,
      lastCards: release.cards?.slice(0, 12),
    };
  }
  return { ...state, releaseByParticleId };
}

export async function loadPersistedOffscreenLibrary(): Promise<OffscreenLibraryState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      memory = normalizeState(JSON.parse(raw) as Partial<OffscreenLibraryState>);
      return memory;
    }
  } catch {
    // keep memory
  }
  return memory;
}

export async function persistOffscreenLibrary(next: OffscreenLibraryState): Promise<void> {
  memory = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // session memory still holds it
  }
}

export function toggleFavoriteInState(
  state: OffscreenLibraryState,
  particleId: string,
): OffscreenLibraryState {
  const id = String(particleId || '').trim();
  if (!id) return state;
  const has = state.favoriteIds.includes(id);
  const favoriteIds = has
    ? state.favoriteIds.filter((x) => x !== id)
    : [...state.favoriteIds, id];
  return { ...state, favoriteIds };
}

export function toggleFeedbackFlagInState(
  state: OffscreenLibraryState,
  particleId: string,
  flag: OffscreenQualityFlag,
): OffscreenLibraryState {
  const id = String(particleId || '').trim();
  if (!id) return state;
  const prev = state.feedbackByParticleId[id];
  const flags = new Set(prev?.flags || []);
  if (flags.has(flag)) flags.delete(flag);
  else flags.add(flag);
  if (flag === 'too_hard') flags.delete('too_easy');
  if (flag === 'too_easy') flags.delete('too_hard');
  const nextFlags = [...flags];
  const feedbackByParticleId = { ...state.feedbackByParticleId };
  if (!nextFlags.length) {
    delete feedbackByParticleId[id];
  } else {
    feedbackByParticleId[id] = {
      particleId: id,
      flags: nextFlags,
      updatedAt: new Date().toISOString(),
    };
  }
  return { ...state, feedbackByParticleId };
}

export const OFFSCREEN_PLAY_MODE_OPTIONS: Array<{
  key: OffscreenPlayMode;
  title: string;
  subtitle: string;
}> = [
  { key: 'newest_first', title: '从最近开始', subtitle: '按知识颗粒创建时间倒序' },
  { key: 'oldest_first', title: '从最远开始', subtitle: '按知识颗粒创建时间正序' },
  { key: 'random_spine', title: '随机播放', subtitle: '在当前学科颗粒中随机跳转' },
  { key: 'favorites', title: '收藏列表', subtitle: '只播你收藏过的条目' },
];

export type OffscreenPlaylistOption = {
  id: string;
  name: string;
  builtin: boolean;
};

/** Built-in + user playlists for the picker. */
export function listOffscreenPlaylistOptions(state: OffscreenLibraryState): OffscreenPlaylistOption[] {
  const extras = state.playlists.map((p) => ({
    id: p.id,
    name: p.name,
    builtin: false,
  }));
  return [
    { id: 'all', name: '全部口播卡', builtin: true },
    { id: 'favorites', name: '收藏列表', builtin: true },
    ...extras,
  ];
}

export function resolveOffscreenPlaylistName(state: OffscreenLibraryState): string {
  const id = state.activePlaylistId || 'all';
  const hit = listOffscreenPlaylistOptions(state).find((p) => p.id === id);
  return hit?.name || '全部口播卡';
}

/** Clear contents of the active playlist (including emptying「全部口播卡」queue view). */
export function clearActivePlaylistContents(state: OffscreenLibraryState): OffscreenLibraryState {
  const id = state.activePlaylistId || 'all';
  if (id === 'all') {
    return { ...state, clearedBuiltinAll: true, generatedCards: [] };
  }
  if (id === 'favorites') {
    return { ...state, favoriteIds: [], clearedBuiltinAll: false };
  }
  const now = new Date().toISOString();
  return {
    ...state,
    clearedBuiltinAll: false,
    playlists: state.playlists.map((p) => (
      p.id === id ? { ...p, particleIds: [], updatedAt: now } : p
    )),
  };
}
